<template>
  <div class="dashboard">
    <section v-if="userStore.isEnterprise" class="page-section hero-section" v-loading="loading">
      <div class="hero-main">
        <div class="hero-copy">
          <div class="hero-kicker">{{ userStore.displayName }}</div>
          <h2 class="hero-title">本周周报</h2>
          <div class="hero-actions">
            <el-button type="primary" size="large" @click="handleImportWord">
              <el-icon><UploadFilled /></el-icon>
              上传 Word 周报
            </el-button>
            <el-button size="large" @click="handlePrimaryAction">
              <el-icon><EditPen /></el-icon>
              在线新建
            </el-button>
            <el-button size="large" plain @click="handleSecondaryAction">
              <el-icon><Document /></el-icon>
              查看全部周报
            </el-button>
          </div>
        </div>

        <div class="hero-stats">
          <div v-for="item in enterpriseHeroStats" :key="item.label" class="hero-stat">
            <div class="hero-stat-label">{{ item.label }}</div>
            <div class="hero-stat-value">{{ item.value }}</div>
          </div>
        </div>
      </div>
    </section>

    <section v-else class="page-section" v-loading="loading">
      <div class="section-head">
        <h2 class="section-title">审核概览</h2>
        <el-button type="primary" @click="handlePrimaryAction">进入审核中心</el-button>
      </div>

      <div class="cards">
        <div v-for="card in adminCardItems" :key="card.label" class="card">
          <div class="card-label">{{ card.label }}</div>
          <div class="card-value">{{ card.value }}</div>
        </div>
      </div>
    </section>

    <section class="page-section">
      <div class="section-head">
        <h2 class="section-title">{{ tableTitle }}</h2>
        <el-button @click="handleSecondaryAction">{{ secondaryActionText }}</el-button>
      </div>

      <el-table v-if="userStore.isAdmin" :data="adminSummary.pending_reports" empty-text="暂无待审核周报">
        <el-table-column prop="enterprise_name" label="企业" min-width="220" />
        <el-table-column prop="report_week" label="周次" width="120" />
        <el-table-column prop="title" label="标题" min-width="280" />
        <el-table-column prop="submitted_at" label="提交时间" width="180" />
        <el-table-column prop="updated_at" label="更新时间" width="180" />
        <el-table-column label="操作" width="120">
          <template #default="{ row }">
            <el-button link type="primary" @click="goReview(row.id)">去审核</el-button>
          </template>
        </el-table-column>
      </el-table>

      <el-table v-else :data="enterpriseSummary.recent_reports" empty-text="还没有周报，先上传一篇">
        <el-table-column prop="report_week" label="周次" width="120" />
        <el-table-column prop="title" label="标题" min-width="280" />
        <el-table-column prop="status" label="状态" width="120">
          <template #default="{ row }">
            <el-tag :type="REPORT_STATUS_TYPE_MAP[row.status]">
              {{ REPORT_STATUS_TEXT_MAP[row.status] }}
            </el-tag>
          </template>
        </el-table-column>
        <el-table-column prop="updated_at" label="更新时间" width="180" />
        <el-table-column label="操作" width="140">
          <template #default="{ row }">
            <el-button link type="primary" @click="$router.push(`/reports/${row.id}`)">继续编辑</el-button>
          </template>
        </el-table-column>
      </el-table>
    </section>
  </div>
</template>

<script setup>
import { computed, onMounted, reactive, ref } from 'vue';
import { ElMessage } from 'element-plus';
import { Document, EditPen, UploadFilled } from '@element-plus/icons-vue';
import { useRouter } from 'vue-router';

import { getAdminReportSummary, getMyReportSummary } from '../../api/report';
import { REPORT_STATUS_TEXT_MAP, REPORT_STATUS_TYPE_MAP } from '../../constants/report';
import { useUserStore } from '../../store/user';

const router = useRouter();
const userStore = useUserStore();
const loading = ref(false);

const enterpriseSummary = reactive({
  total_reports: 0,
  draft_count: 0,
  submitted_count: 0,
  approved_count: 0,
  rejected_count: 0,
  total_views: 0,
  recent_reports: []
});

const adminSummary = reactive({
  total_reports: 0,
  enterprise_count: 0,
  draft_count: 0,
  submitted_count: 0,
  approved_count: 0,
  rejected_count: 0,
  total_views: 0,
  pending_reports: []
});

const adminCardItems = computed(() => [
  { label: '待审核', value: adminSummary.submitted_count },
  { label: '已通过', value: adminSummary.approved_count },
  { label: '已驳回', value: adminSummary.rejected_count },
  { label: '企业数', value: adminSummary.enterprise_count },
  { label: '总周报', value: adminSummary.total_reports },
  { label: '累计阅读', value: adminSummary.total_views }
]);

const enterpriseHeroStats = computed(() => [
  { label: '草稿', value: enterpriseSummary.draft_count },
  { label: '待审核', value: enterpriseSummary.submitted_count },
  { label: '已发布', value: enterpriseSummary.approved_count },
  { label: '总阅读', value: enterpriseSummary.total_views }
]);

const tableTitle = computed(() => (userStore.isAdmin ? '待处理周报' : '最近周报'));
const secondaryActionText = computed(() => (userStore.isAdmin ? '查看全部审核任务' : '进入周报列表'));

const handlePrimaryAction = () => {
  router.push(userStore.isAdmin ? '/reviews' : '/reports/new');
};

const handleImportWord = () => {
  router.push({ path: '/reports/new', query: { mode: 'word' } });
};

const handleSecondaryAction = () => {
  router.push(userStore.isAdmin ? '/reviews' : '/reports');
};

const goReview = (id) => {
  router.push({ name: 'reviews', query: { id: String(id) } });
};

const loadDashboard = async () => {
  loading.value = true;
  try {
    if (userStore.isAdmin) {
      const result = await getAdminReportSummary();
      Object.assign(adminSummary, result.data);
    } else {
      const result = await getMyReportSummary();
      Object.assign(enterpriseSummary, result.data);
    }
  } catch (error) {
    ElMessage.error(error.response?.data?.message || '获取工作台数据失败');
  } finally {
    loading.value = false;
  }
};

onMounted(loadDashboard);
</script>

<style scoped>
.dashboard {
  display: grid;
  gap: 20px;
}

.hero-section {
  padding: 24px;
}

.hero-main {
  display: grid;
  grid-template-columns: minmax(0, 1.2fr) minmax(320px, 0.8fr);
  gap: 20px;
  align-items: stretch;
}

.hero-copy {
  display: grid;
  gap: 16px;
  align-content: start;
}

.hero-kicker {
  color: #2563eb;
  font-size: 13px;
  font-weight: 600;
}

.hero-title {
  margin: 0;
  font-size: 28px;
  line-height: 1.2;
}

.hero-actions {
  display: flex;
  gap: 12px;
  flex-wrap: wrap;
}

.hero-stats {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 12px;
}

.hero-stat,
.card {
  min-width: 0;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  background: #f9fafb;
  padding: 18px;
}

.hero-stat-label,
.card-label {
  color: #6b7280;
  font-size: 13px;
  line-height: 1.5;
}

.hero-stat-value,
.card-value {
  margin-top: 8px;
  font-size: 28px;
  font-weight: 700;
  line-height: 1.2;
  color: #111827;
}

.section-head {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 16px;
  margin-bottom: 16px;
  flex-wrap: wrap;
}

.cards {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
  gap: 16px;
}

@media (max-width: 960px) {
  .hero-main {
    grid-template-columns: 1fr;
  }
}

@media (max-width: 768px) {
  .hero-section {
    padding: 16px;
  }

  .hero-title {
    font-size: 24px;
  }

  .hero-stats {
    grid-template-columns: 1fr 1fr;
  }
}
</style>
